import { Injectable, type Renderer2, RendererFactory2 } from '@angular/core';
import { type HtmlScriptTagListItem } from '../models/dom/html-script-tag-list-item';
import { type HtmlScriptTagOptions } from '../models/dom/html-script-tag-options';

@Injectable({
    providedIn: 'root',
})
export class DomService {
    private readonly scriptTagList: Record<string, HtmlScriptTagListItem> = {};
    private readonly renderer: Renderer2;

    public constructor(private readonly rendererFactory: RendererFactory2) {
        // eslint-disable-next-line no-null/no-null
        this.renderer = rendererFactory.createRenderer(undefined, null);
    }

    public createScriptTag(src: string, options: HtmlScriptTagOptions): HTMLScriptElement | undefined {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition,sonarjs/different-types-comparison
        if (this.scriptTagList[src] !== undefined) {
            this.scriptTagList[src].usageCount += 1;

            return undefined;
        }

        const target: HTMLElement = options.target;
        const srcElement: HTMLScriptElement | null = target.querySelector(`[src="${src}"]`);

        // eslint-disable-next-line no-null/no-null
        if (srcElement !== null) {
            this.addScriptTagToList(src, srcElement, target);

            return undefined;
        }

        const element: HTMLScriptElement = this.renderer.createElement('script');
        element.src = src;
        element.type = options.type ?? 'text/javascript';
        element.async = options.async ?? false;

        if (options.id !== undefined) {
            element.id = options.id;
        }

        for (const key of Object.keys(options.data ?? {})) {
            // eslint-disable-next-line no-null/no-null, @typescript-eslint/no-unnecessary-condition,sonarjs/different-types-comparison
            if (options.data?.[key] === null) {
                continue;
            }

            element.dataset[key] = options.data?.[key] ?? '';
        }

        this.renderer.appendChild(target, element);
        this.addScriptTagToList(src, element, target);

        return element;
    }

    public removeScriptTag(src: string): void {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition,sonarjs/different-types-comparison
        if (this.scriptTagList[src] === undefined) {
            return;
        }

        const currentUsage: number = this.scriptTagList[src].usageCount;

        if (currentUsage === 1) {
            this.renderer.removeChild(this.scriptTagList[src].target, this.scriptTagList[src].element);
            delete this.scriptTagList[src]; // eslint-disable-line @typescript-eslint/no-dynamic-delete

            return;
        }

        this.scriptTagList[src].usageCount -= 1;
    }

    private addScriptTagToList(src: string, element: HTMLScriptElement, target: HTMLElement): void {
        this.scriptTagList[src] = {
            src,
            element,
            target,
            usageCount: 1,
        };
    }
}
